import type { ReactNode } from 'react';
import { History, Network } from 'lucide-react';
import { Card, EmptyState, Fact, Facts, TableSkeleton } from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import type { ZoneSectionProps } from '@/components/sites/dns/ZoneSections';
import { useNameServerHistory } from '@/hooks/useDomains';
import { formatDateTime, formatRelative } from '@/utils/format';

const REGISTRATION: Record<string, { label: string; tone: string }> = {
  ACTIVE: { label: 'активна', tone: 'tone-healthy' },
  EXPIRED: { label: 'истекла', tone: 'tone-down' },
  TRANSFER_PENDING: { label: 'идёт перенос к другому регистратору', tone: 'tone-degraded' },
  SUSPENDED: { label: 'приостановлена регистратором', tone: 'tone-down' },
  ERROR: { label: 'ошибка у регистратора', tone: 'tone-down' },
  UNKNOWN: { label: 'неизвестно', tone: 'muted' },
};

const NS_SOURCES: Record<string, string> = {
  CLOUDFLARE: 'Cloudflare',
  REGISTRAR_DEFAULT: 'по умолчанию у регистратора',
  MANUAL: 'вручную',
  IMPORTED: 'при импорте',
  RESTORED: 'восстановлены',
};

const DAY = 86_400_000;

/** The same servers, whatever their order and case. */
function sameServers(a: readonly string[], b: readonly string[]): boolean {
  const norm = (list: readonly string[]) => list.map((name) => name.toLowerCase().replace(/\.$/, '')).sort().join(' ');
  return a.length > 0 && norm(a) === norm(b);
}

/**
 * Who holds the domain, until when, and which name servers answer for it —
 * read only. Name servers are changed in 2d2s: a wrong set takes the site and
 * the domain's mail down at once, which is not a thing to do from a site page.
 */
export function NameServersSection({ siteId, zone }: ZoneSectionProps): ReactNode {
  const history = useNameServerHistory(siteId, zone.domainId ?? '', zone.domainId !== null);
  const registration = zone.registration;
  const status = registration?.status ? (REGISTRATION[registration.status] ?? { label: registration.status, tone: '' }) : null;
  const left = registration?.expiresAt ? Math.floor((new Date(registration.expiresAt).getTime() - Date.now()) / DAY) : null;
  const matches = zone.cloudflare && registration ? sameServers(registration.nameServers, zone.nameServers) : null;

  return (
    <div className="stack">
      <Card title="Регистрация и NS" icon={Network}>
        {registration ? (
          <Facts>
            <Fact label="Регистратор">
              {registration.registrar ?? '—'}
              {registration.registrarAccount ? <span className="muted"> · {registration.registrarAccount}</span> : null}
            </Fact>
            <Fact label="Регистрация">{status ? <span className={status.tone}>{status.label}</span> : '—'}</Fact>
            <Fact label="Оплачен до">
              {registration.expiresAt ? (
                <span className={left !== null && left < 30 ? 'tone-down' : ''}>
                  {formatDateTime(registration.expiresAt)}
                  {left !== null ? <span className={left < 30 ? '' : 'muted'}> · {left >= 0 ? `осталось ${left} дн.` : 'истёк'}</span> : null}
                </span>
              ) : (
                '—'
              )}
            </Fact>
            <Fact label="Автопродление">
              {registration.autoRenew === null ? '—' : registration.autoRenew ? 'включено' : <span className="tone-degraded">выключено</span>}
            </Fact>
            <Fact label="Защита от переноса">{registration.locked === null ? '—' : registration.locked ? 'включена' : 'выключена'}</Fact>
            <Fact label="NS у регистратора">
              <div className="stack stack-tight">
                {registration.nameServers.length > 0 ? (
                  registration.nameServers.map((name) => (
                    <span key={name} className="mono">
                      {name}
                    </span>
                  ))
                ) : (
                  <span className="faint">неизвестны</span>
                )}
                {registration.nameServersChangedAt ? <span className="muted text-sm">изменены {formatRelative(registration.nameServersChangedAt)}</span> : null}
              </div>
            </Fact>
            {zone.cloudflare ? (
              <Fact label="NS зоны Cloudflare">
                <div className="stack stack-tight">
                  {zone.nameServers.map((name) => (
                    <span key={name} className="mono">
                      {name}
                    </span>
                  ))}
                  {matches === true ? (
                    <span className="text-sm tone-healthy">совпадают с NS регистратора</span>
                  ) : matches === false ? (
                    <span className="text-sm tone-down">
                      не совпадают с NS регистратора: настройки Cloudflare не действуют
                    </span>
                  ) : null}
                </div>
              </Fact>
            ) : null}
          </Facts>
        ) : (
          <EmptyState icon={Network} message="Нет данных о регистрации" />
        )}
        <p className="field-hint" style={{ marginBottom: 0 }}>
          NS изменяются в 2d2s.
        </p>
      </Card>

      <Card flush title="История NS" icon={History}>
        {history.isPending ? (
          <TableSkeleton rows={2} columns={3} />
        ) : history.isError ? (
          <ErrorState error={history.error} fallback="Не удалось прочитать историю из 2d2s" />
        ) : history.data.length === 0 ? (
          <EmptyState icon={History} message="Смен NS не было" />
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Когда</th>
                  <th scope="col">NS</th>
                  <th scope="col">Откуда</th>
                </tr>
              </thead>
              <tbody>
                {history.data.map((change, index) => (
                  <tr key={`${change.at ?? ''}-${index}`}>
                    <td className="nowrap">{formatDateTime(change.at)}</td>
                    <td className="mono">{change.nameServers.join(', ')}</td>
                    <td>
                      {NS_SOURCES[change.source] ?? change.source}
                      {change.label ? <span className="muted text-sm"> · {change.label}</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
